/**
 * Season pricing (DK, _qa/seasons/spec.md). Shared by the booking module (myselfie-booking-calendar.js), the product
 * pages (mys-pdp.js), the cart page (mys-cart.js) and the checkout kit (myselfie-checkout.js). Vanilla ES5.
 *
 * Data: window.myselfie.seasons = {default: 'Mid', periods: [{season, from, to}], cart: {check}} from the
 * "Sæsonpriser" section (sections/mys-seasons.liquid, DK footer group). Read lazily on every call, so the order the
 * scripts load in does not matter. Without the section (SE/DE) every function is a no-op for products without a
 * "Sæson" option, and a product with one falls back to its first season value.
 *
 * Product shapes understood: the selector JSON ({options: [names], variants: [{options: [...]}]}), /products/x.js
 * ({options: [names] | [{name}], variants: [{options}]}), /products.json ({options: [{name}], variants: [{option1..3}]}).
 * Cart lines: /cart.js items (options_with_values [{name, value}]).
 *
 *   window.myselfie.seasonKit = {
 *     isSeasonName, seasonIndex, durationIndex, durationOf, seasonOf, optionsOf, same, norm, parseDate,
 *     config, seasonFor, seasonDays, pick, visibleTitle, lineSeason, lineTitle, lineOptions,
 *     bundleSkuParts, seasonBundleLine, seasonBundleProduct (season bundles, sold whole),
 *     planFix, fixCart, issues, hideCss
 *   }
 */
(function () {
  'use strict';

  var M = (window.myselfie = window.myselfie || {});
  if (M.seasonKit) return;

  /* Option names that mean "season" (case, spaces, æ/ä and hyphens do not matter). */
  var NAMES = ['saeson', 'season', 'sasong', 'saison'];
  var DURATION_NAME = /varighed|duration|lejeperiode|hyr(es)?period|mietdauer|dauer|rental/i;

  /** Customer-facing text from window.myselfie.i18n (snippets/myselfie-i18n-seasons.liquid), Danish fallback. */
  function t(ns, key, fallback, vars) {
    var s = window.myselfie && window.myselfie.i18n && window.myselfie.i18n[ns] && window.myselfie.i18n[ns][key];
    if (s == null || s === '') s = fallback;
    if (vars) for (var k in vars) s = String(s).split('[' + k + ']').join(vars[k]);
    return s;
  }

  function fold(s) {
    return String(s == null ? '' : s)
      .toLowerCase()
      .replace(/æ/g, 'ae')
      .replace(/[äåáà]/g, 'a')
      .replace(/[øö]/g, 'o')
      .replace(/[\s_\-.]+/g, '');
  }
  /** Comparable form of a season (or option) name: "Super high" = "super-high" = "SUPERHIGH". */
  function norm(s) {
    return fold(s);
  }
  function same(a, b) {
    return !!fold(a) && fold(a) === fold(b);
  }
  function isSeasonName(name) {
    return NAMES.indexOf(fold(name)) > -1;
  }
  function optionNames(p) {
    var o = (p && p.options) || [];
    return o.map(function (x) { return typeof x === 'string' ? x : (x && x.name) || ''; });
  }
  /** Option values of a variant (array form, or option1..3). */
  function optionsOf(v) {
    if (!v) return [];
    if (Array.isArray(v.options)) return v.options;
    var out = [];
    ['option1', 'option2', 'option3'].forEach(function (k) { if (v[k] != null) out.push(v[k]); });
    return out;
  }
  /** Index of the product's season option, -1 when it has none. */
  function seasonIndex(p) {
    var n = optionNames(p);
    for (var i = 0; i < n.length; i++) if (isSeasonName(n[i])) return i;
    return -1;
  }
  /**
   * Index of the rental period option: a known period name (Varighed / Hyresperiod / Hyrperiod / Mietdauer ...), else by
   * position: the first option that is not the season (the booth products' period is their first option in every store).
   */
  function durationIndex(p) {
    var n = optionNames(p);
    var s = seasonIndex(p);
    for (var i = 0; i < n.length; i++) if (i !== s && DURATION_NAME.test(n[i])) return i;
    for (var j = 0; j < n.length; j++) if (j !== s) return j;
    return -1;
  }
  function durationOf(v, p) {
    var o = optionsOf(v);
    var i = durationIndex(p);
    return String((i > -1 ? o[i] : o[0]) || '');
  }
  function seasonOf(v, p) {
    var i = seasonIndex(p);
    return i > -1 ? String(optionsOf(v)[i] || '') : '';
  }

  /* ---------- the calendar ---------- */

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }
  /** "2026-12-24", "24-12-2026" (also with / or .) -> "2026-12-24"; '' when it is not a real date. */
  function parseDate(text) {
    var t = String(text == null ? '' : text).trim();
    var m = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(t);
    var y, mo, d;
    if (m) { y = +m[1]; mo = +m[2]; d = +m[3]; }
    else {
      m = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/.exec(t);
      if (!m) return '';
      d = +m[1]; mo = +m[2]; y = +m[3];
    }
    if (mo < 1 || mo > 12 || d < 1) return '';
    var days = new Date(y, mo, 0).getDate();
    if (d > days) return '';
    return y + '-' + pad(mo) + '-' + pad(d);
  }
  var cache = { src: null, cfg: null };
  /** {def, periods: [{season, from, to}]} - only valid periods, in the editor's order (first match wins). */
  function config() {
    var src = M.seasons || null;
    if (cache.src === src && cache.cfg) return cache.cfg;
    var cfg = { def: '', periods: [], present: !!src };
    if (src) {
      cfg.def = String(src['default'] || '').trim();
      (src.periods || []).forEach(function (p) {
        if (!p) return;
        var season = String(p.season || '').trim();
        var from = parseDate(p.from);
        var to = parseDate(p.to);
        if (season && from && to && from <= to) cfg.periods.push({ season: season, from: from, to: to });
      });
    }
    cache = { src: src, cfg: cfg };
    return cfg;
  }
  /** Season of a date (YYYY-MM-DD): the first period that holds it, else the default season ('' = none). */
  function seasonFor(iso) {
    var c = config();
    if (iso) {
      for (var i = 0; i < c.periods.length; i++) {
        var p = c.periods[i];
        if (iso >= p.from && iso <= p.to) return p.season;
      }
    }
    return c.def;
  }
  function toIso(d) {
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }
  /**
   * One representative day per stretch of days that price the same: keyFn(iso) -> key (e.g. booth product + season).
   * Days from `from` to `to` (YYYY-MM-DD, inclusive). Used for "fra" prices: min over these days = min over every day.
   */
  function seasonDays(from, to, keyFn) {
    var out = [];
    var seen = {};
    var p = from.split('-');
    var d = new Date(+p[0], +p[1] - 1, +p[2]);
    for (var n = 0; n < 4000; n++) {
      var iso = toIso(d);
      if (iso > to) break;
      var k = keyFn ? keyFn(iso) : norm(seasonFor(iso));
      if (!seen.hasOwnProperty(k)) {
        seen[k] = true;
        out.push(iso);
      }
      d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
    }
    return out;
  }

  /* ---------- variants ---------- */

  /**
   * The variant to sell: want = {duration, type, season, iso, others: {index: value}}.
   * Rental period first (any non-season option equal to it), then the other wanted values, then the season of the
   * date (want.season wins, else seasonFor(want.iso)); a season the product does not have falls back to the
   * default season, then to the first remaining variant (the product's own order).
   * Returns null only for a product without variants; want.strict = null when the duration does not exist.
   */
  function pick(p, want) {
    if (!p || !p.variants || !p.variants.length) return null;
    want = want || {};
    var sIdx = seasonIndex(p);
    var cands = p.variants.slice();
    var nonSeason = function (v, value) {
      var o = optionsOf(v);
      for (var i = 0; i < o.length; i++) if (i !== sIdx && o[i] === value) return true;
      return false;
    };
    if (want.duration) {
      var d = cands.filter(function (v) { return nonSeason(v, want.duration); });
      if (d.length) cands = d;
      else if (want.strict) return null;
    }
    if (want.type) {
      var t = cands.filter(function (v) { return nonSeason(v, want.type); });
      if (t.length) cands = t;
    }
    if (want.others) {
      Object.keys(want.others).forEach(function (k) {
        var o = cands.filter(function (v) { return optionsOf(v)[+k] === want.others[k]; });
        if (o.length) cands = o;
      });
    }
    if (sIdx < 0) return cands[0];
    var season = want.season != null && want.season !== '' ? want.season : seasonFor(want.iso || '');
    var bySeason = function (s) {
      if (!s) return null;
      for (var i = 0; i < cands.length; i++) if (same(optionsOf(cands[i])[sIdx], s)) return cands[i];
      return null;
    };
    return bySeason(season) || bySeason(config().def) || cands[0];
  }
  /** Variant title for customers: the option values without the season ("Weekend"), '' for a default title. */
  function visibleTitle(v, p) {
    var o = optionsOf(v);
    var s = seasonIndex(p);
    if (!o.length) {
      var t = v && v.title;
      return t && t !== 'Default Title' ? String(t) : '';
    }
    var out = o.filter(function (x, i) { return i !== s; }).join(' / ');
    return out === 'Default Title' ? '' : out;
  }

  /* ---------- cart lines (/cart.js) ---------- */

  /** [{name, value}] of a cart line (options_with_values; variant_options without names otherwise). */
  function lineOptions(it) {
    if (it && Array.isArray(it.options_with_values)) return it.options_with_values;
    return ((it && it.variant_options) || []).map(function (v) { return { name: '', value: v }; });
  }
  /** {index, value} of the season option of a cart line, or null. */
  function lineSeason(it) {
    var o = lineOptions(it);
    for (var i = 0; i < o.length; i++) if (isSeasonName(o[i].name)) return { index: i, value: String(o[i].value || '') };
    return null;
  }
  /** Variant title of a cart line without the season. */
  function lineTitle(it) {
    var o = lineOptions(it);
    if (!o.length || !o.some(function (x) { return x.name; })) {
      var t = it && it.variant_title;
      return t && t !== 'Default Title' ? String(t) : '';
    }
    var out = o.filter(function (x) { return !isSeasonName(x.name); }).map(function (x) { return x.value; }).join(' / ');
    return out === 'Default Title' ? '' : out;
  }
  /**
   * Season bundles (owner 2026-10-08): a bundle product with a "Season" option (Varighed x Season, SKUs M-W+MYG,
   * M-W+MYP, M-W+MYG+MYP - no print type or logo in the SKU) is sold WHOLE: the bundle variant stays in the cart
   * (Date, print_mode, _reason) next to its "Logo på print" line and extra rolls; it is never split into booth +
   * printer + Mygreetings. Old bundles (no season option, SKUs M-W+MYP-1-P-ML ...) are still split.
   * A SKU part: {printer, myg} of a bundle SKU, null when the SKU is not a bundle ("+").
   */
  function bundleSkuParts(sku) {
    var s = String(sku || '').trim().toUpperCase();
    if (s.indexOf('+') < 0) return null;
    var parts = s.split('+');
    return {
      printer: parts.some(function (p) { return /^MYP(?:-|$)/.test(p.trim()); }),
      myg: parts.some(function (p) { return /^MYG(?:-|$)/.test(p.trim()); }),
    };
  }
  /** {printer, myg} for a cart line (/cart.js) of a season bundle, else null. */
  function seasonBundleLine(it) {
    if (!it || !lineSeason(it)) return null;
    return bundleSkuParts(it.sku);
  }
  /** {printer, myg} for a product (any shape) that is a season bundle, else null. */
  function seasonBundleProduct(p) {
    if (!p || seasonIndex(p) < 0 || !p.variants || !p.variants.length) return null;
    return bundleSkuParts(p.variants[0].sku);
  }
  function displayToIso(s) {
    var m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(s || '').trim());
    return m ? m[3] + '-' + m[2] + '-' + m[1] : '';
  }
  function isAuto(it) {
    var p = (it && it.properties) || {};
    return Object.keys(p).some(function (k) { return k.indexOf('__upcart') === 0; });
  }
  /** The cart's booking date (the first line with a Date), YYYY-MM-DD or ''. */
  function cartIso(cart) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var iso = displayToIso(items[i].properties && items[i].properties.Date);
      if (iso) return iso;
    }
    return '';
  }

  /**
   * Pure: what has to change so every line of a product with a "Sæson" option carries the season of its date
   * (its own Date, else the cart's booking date, else the default season). products: handle -> product
   * (/products/x.js shape). Returns {ops: [{line, target, iso, season}], nodate: [line], unresolved: [line]}.
   * nodate = a season line in a cart without any date (the season is the default; a date is required).
   */
  function planFix(cart, products) {
    var out = { ops: [], nodate: [], unresolved: [], need: [] };
    var c = config();
    if (!c.present) return out;
    var booking = cartIso(cart);
    ((cart && cart.items) || []).forEach(function (it) {
      if (isAuto(it)) return;
      var ls = lineSeason(it);
      if (!ls) return;
      var iso = displayToIso(it.properties && it.properties.Date) || booking;
      if (!iso) out.nodate.push(it);
      var want = seasonFor(iso);
      if (!want || same(ls.value, want)) return;
      var p = products && products[it.handle];
      if (!p) {
        out.need.push(it.handle);
        return;
      }
      var src = null;
      for (var i = 0; i < p.variants.length; i++) if (p.variants[i].id === it.variant_id) src = p.variants[i];
      var sIdx = seasonIndex(p);
      if (sIdx < 0) return; // the product lost its season option: nothing to compare
      var base = src ? optionsOf(src) : lineOptions(it).map(function (x) { return x.value; });
      var target = null;
      var bySeason = function (s) {
        for (var j = 0; j < p.variants.length; j++) {
          var o = optionsOf(p.variants[j]);
          var ok = same(o[sIdx], s);
          for (var k = 0; ok && k < o.length; k++) if (k !== sIdx && o[k] !== base[k]) ok = false;
          if (ok) return p.variants[j];
        }
        return null;
      };
      target = bySeason(want) || bySeason(c.def);
      if (!target) out.unresolved.push(it);
      else if (target.id !== it.variant_id) out.ops.push({ line: it, target: target, iso: iso, season: want });
    });
    return out;
  }

  function rootUrl(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + path;
  }
  function getJson(path) {
    return fetch(rootUrl(path), { headers: { Accept: 'application/json' }, cache: 'no-store' }).then(function (r) {
      if (!r.ok) throw { status: r.status };
      return r.json();
    });
  }
  function post(path, body) {
    var kit = M.checkoutKit;
    if (kit && kit.cartPost) return kit.cartPost(path, body);
    return fetch(rootUrl(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json().then(function (b) {
        if (!r.ok) throw { status: r.status, message: b.description || b.message };
        return b;
      });
    });
  }
  function getCart() {
    var kit = M.checkoutKit;
    return kit && kit.cartGet ? kit.cartGet() : getJson('cart.js');
  }
  var productCache = {};
  function loadProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = getJson('products/' + encodeURIComponent(handle) + '.js').catch(function (e) {
        delete productCache[handle];
        throw e;
      });
    }
    return productCache[handle];
  }
  function shortDate(iso) {
    var p = String(iso || '').split('-');
    return p.length === 3 ? p[2] + '/' + p[1] : '';
  }

  /**
   * Brings the season of every season line in line with its date: the right variant is added (same quantity and
   * properties) and the old line removed. Resolves {cart, changed: [{title, iso, from, to}], nodate, unresolved,
   * notes: [text]} - never rejects for a product it cannot read (that line is "unresolved").
   */
  function fixCart(cart) {
    var first = planFix(cart, {});
    if (!first.need.length && !first.ops.length) {
      return Promise.resolve({ cart: cart, changed: [], nodate: first.nodate, unresolved: first.unresolved, notes: [] });
    }
    var handles = first.need.filter(function (h, i, a) { return a.indexOf(h) === i; });
    var products = {};
    return Promise.all(handles.map(function (h) {
      return loadProduct(h).then(function (p) { products[h] = p; }, function () { /* unresolved below */ });
    })).then(function () {
      var plan = planFix(cart, products);
      handles.forEach(function (h) {
        if (products[h]) return;
        (cart.items || []).forEach(function (it) { if (it.handle === h && lineSeason(it) && plan.unresolved.indexOf(it) < 0) plan.unresolved.push(it); });
      });
      if (!plan.ops.length) return { cart: cart, changed: [], nodate: plan.nodate, unresolved: plan.unresolved, notes: [] };
      var changed = [];
      var failed = [];
      var chain = Promise.resolve();
      plan.ops.forEach(function (op) {
        chain = chain.then(function () {
          var add = { id: op.target.id, quantity: op.line.quantity };
          var props = op.line.properties || {};
          if (Object.keys(props).length) add.properties = props;
          return post('cart/add.js?upcart=1&opens_cart=never', { items: [add] })
            .then(function () { return post('cart/change.js', { id: op.line.key, quantity: 0 }); })
            .then(function () {
              changed.push({ title: op.line.product_title, iso: op.iso, from: op.line.price, to: op.target.price });
            }, function () { failed.push(op.line); });
        });
      });
      return chain.then(getCart).then(function (next) {
        var notes = [];
        changed.forEach(function (c) {
          var text = t('seasons', 'price_updated', 'Prisen er opdateret til den gældende pris for d. [date].', { date: shortDate(c.iso) });
          if (c.from !== c.to && notes.indexOf(text) < 0) notes.push(text);
        });
        try { document.dispatchEvent(new CustomEvent('myselfie:season-fix', { detail: { changed: changed, cart: next } })); } catch (e) { /* old browser */ }
        return { cart: next, changed: changed, nodate: plan.nodate, unresolved: plan.unresolved.concat(failed), notes: notes };
      });
    });
  }
  /** Checkout blockers of a fix result: [{key, message}]. */
  function issues(result) {
    var out = [];
    if (!result) return out;
    if (result.unresolved && result.unresolved.length) {
      out.push({ key: 'season', message: t('seasons', 'price_unresolved', 'Prisen på [title] kunne ikke opdateres til jeres dato. Prøv igen om et øjeblik, eller fjern varen.', { title: result.unresolved.map(function (it) { return it.product_title; }).join(', ') }) });
    }
    if (result.nodate && result.nodate.length) {
      out.push({ key: 'season-date', message: t('seasons', 'need_date', 'Vælg datoen for jeres fest, før I går til betaling – prisen afhænger af datoen.') });
    }
    return out;
  }

  /* ---------- hide the season from customers: the Upcart drawer (options rendered as data attributes) ---------- */

  var CSS_NAMES = ['Sæson', 'sæson', 'SÆSON', 'Saeson', 'saeson', 'Season', 'season', 'SEASON', 'Säsong', 'Saison'];
  var hideCss = CSS_NAMES.map(function (n) {
    return '.upcart-item-option[data-upcart-item-option-category="' + n + '" i]';
  }).join(',') + '{display:none!important}';
  function injectCss(root) {
    try {
      if (!root || root.querySelector('style[data-mys-season-css]')) return;
      var st = document.createElement('style');
      st.setAttribute('data-mys-season-css', '');
      st.textContent = hideCss;
      (root.head || root).appendChild(st);
    } catch (e) { /* ignore */ }
  }
  function hideInApps() {
    injectCss(document);
    // Upcart can render into a shadow root: style it there too, and rewrite option text the CSS cannot reach
    var hosts = document.querySelectorAll('#upCart, [id^="upcart"], upcart-cart, .upcart-root');
    Array.prototype.forEach.call(hosts, function (h) { if (h.shadowRoot) injectCss(h.shadowRoot); });
  }
  function watchApps() {
    hideInApps();
    if (!window.MutationObserver || !document.body) return;
    var pending = 0;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () { pending = 0; hideInApps(); }, 300);
    }).observe(document.body, { childList: true }); // app roots are appended to <body>
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchApps);
  else watchApps();

  /* ---------- guard on every other page: a season line that does not match its date is corrected ---------- */

  function toast(text) {
    var el = document.createElement('div');
    el.className = 'mys-season-note';
    el.setAttribute('role', 'status');
    el.textContent = text;
    el.style.cssText = 'position:fixed;left:50%;bottom:20px;transform:translateX(-50%);z-index:2147483000;max-width:min(92vw,520px);' +
      'background:#e8f8f4;color:#1d2b29;border:1px solid #62d3af;border-radius:14px;padding:12px 18px;font:500 15px/1.4 Poppins,system-ui,sans-serif;box-shadow:0 6px 24px rgba(0,0,0,.12)';
    document.body.appendChild(el);
    setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 9000);
  }
  function guard() {
    var s = M.seasons;
    if (!s || !s.cart || !s.cart.check) return;
    // the booking module and the cart page check the cart themselves (inside their own serialised cart queue)
    if (document.querySelector('myselfie-product-selector, [data-mys-cart]')) return;
    // elsewhere one writer at a time with the global cart rules (assets/myselfie-cart-rules.js, KIT.serial)
    var kit = M.checkoutKit;
    var job = function () { return getCart().then(fixCart); };
    (kit && kit.serial ? kit.serial(job) : job()).then(function (r) {
      if (!r.changed.length) return;
      try {
        var detail = kit && kit.cartDetail ? kit.cartDetail(r.cart) : { item_count: r.cart.item_count, items_subtotal_price: r.cart.items_subtotal_price };
        document.dispatchEvent(new CustomEvent('myselfie:cart', { detail: detail }));
      } catch (e) { /* old browser */ }
      if (r.notes.length) toast(r.notes.join(' '));
    }).catch(function () { /* the cart page / checkout guard tries again */ });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { setTimeout(guard, 0); });
  else setTimeout(guard, 0);

  M.seasonKit = {
    NAMES: NAMES,
    norm: norm,
    same: same,
    isSeasonName: isSeasonName,
    optionNames: optionNames,
    optionsOf: optionsOf,
    seasonIndex: seasonIndex,
    durationIndex: durationIndex,
    durationOf: durationOf,
    seasonOf: seasonOf,
    parseDate: parseDate,
    config: config,
    seasonFor: seasonFor,
    seasonDays: seasonDays,
    pick: pick,
    visibleTitle: visibleTitle,
    lineOptions: lineOptions,
    lineSeason: lineSeason,
    lineTitle: lineTitle,
    cartIso: cartIso,
    bundleSkuParts: bundleSkuParts,
    seasonBundleLine: seasonBundleLine,
    seasonBundleProduct: seasonBundleProduct,
    planFix: planFix,
    fixCart: fixCart,
    issues: issues,
    hideCss: hideCss,
  };
})();
